import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Button,
  CloseButton,
  NumberInput,
  NumberInputField,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Calendar, DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";
import "react-multi-date-picker/styles/colors/teal.css";
import { todayDateInCET } from "../../utils/helpers";
import {
  getDayOccupancy,
  maxBlockableForRange,
  normalizeReservations,
  rangesOverlap,
} from "../../utils/vehicleStockOccupancy";
import "./VehicleUnavailableDates.scss";

const rangeKey = (start, end) => `${start}|${end}`;

const fmt = (value) => new DateObject(value).format();

const formatCalendarRanges = (ranges) =>
  ranges
    .filter((range) => range?.length === 2)
    .map((range) => [
      new DateObject(range[0]).format(),
      new DateObject(range[1]).format(),
    ]);

const eachDayInRange = (start, end, fn) => {
  let current = new DateObject(start);
  const endDate = new DateObject(end);
  while (current <= endDate) {
    fn(current.format());
    current = current.add(1, "day");
  }
};

/**
 * Stock-aware unavailable-dates manager for a vehicle.
 * Calendar: red = owner blocks, gray disabled = fully booked days.
 * Detail lists below the calendar show reservation/block quantities.
 *
 * Calls onChange with owner blocks as `[[start, end, quantity], ...]`.
 */
function VehicleUnavailableDates({
  bookedDates,
  dateRangesFromDatabase = [],
  stockCount = 1,
  onChange,
}) {
  const { t } = useTranslation("common");
  const stock = Number(stockCount) || 1;

  const reservations = useMemo(
    () => normalizeReservations(bookedDates, fmt),
    [bookedDates]
  );

  const reservedRanges = useMemo(
    () => reservations.map((r) => [r.start, r.end]),
    [reservations]
  );

  const initialBlocks = useMemo(
    () =>
      (dateRangesFromDatabase || []).map((d) => [
        fmt(d.startDate),
        fmt(d.endDate),
      ]),
    [dateRangesFromDatabase]
  );

  const initialQuantities = useMemo(() => {
    const quantities = {};
    (dateRangesFromDatabase || []).forEach((d) => {
      quantities[rangeKey(fmt(d.startDate), fmt(d.endDate))] =
        d.quantity || stock;
    });
    return quantities;
  }, [dateRangesFromDatabase, stock]);

  const [ownerBlockRanges, setOwnerBlockRanges] = useState(initialBlocks);
  const [quantities, setQuantities] = useState(initialQuantities);
  const [calendarActiveDate, setCalendarActiveDate] = useState(
    () => new DateObject(todayDateInCET())
  );

  const isReservedRange = ([start, end]) =>
    reservedRanges.some(([rStart, rEnd]) => rStart === start && rEnd === end);

  const ownerBlocksWithQty = useMemo(
    () =>
      ownerBlockRanges.map(([start, end]) => ({
        start,
        end,
        quantity: quantities[rangeKey(start, end)] || stock,
      })),
    [ownerBlockRanges, quantities, stock]
  );

  const calendarValue = useMemo(() => ownerBlockRanges, [ownerBlockRanges]);

  const getMaxBlockable = (start, end) => {
    const max = maxBlockableForRange(start, end, {
      stock,
      reservations,
      ownerBlocks: ownerBlocksWithQty,
      excludeRange: { start, end },
    });
    return stock === 1 ? Math.max(1, max) : max;
  };

  const getOccupancy = (strDate) =>
    getDayOccupancy(strDate, {
      stock,
      reservations,
      ownerBlocks: ownerBlocksWithQty,
    });

  const isDayLockedForNewBlock = (strDate) => {
    const { booked, blocked, available } = getOccupancy(strDate);
    if (stock === 1) return booked > 0;
    if (blocked > 0) return false;
    return available === 0;
  };

  const rangeIncludesLockedDay = (start, end) => {
    let locked = false;
    eachDayInRange(start, end, (strDate) => {
      if (isDayLockedForNewBlock(strDate)) locked = true;
    });
    return locked;
  };

  const canBlockRange = (start, end, otherOwnerRanges = ownerBlockRanges) => {
    if (stock === 1) {
      return !reservedRanges.some(([rStart, rEnd]) =>
        rangesOverlap(start, end, rStart, rEnd)
      );
    }

    const otherBlocks = otherOwnerRanges
      .filter(([s, e]) => s !== start || e !== end)
      .map(([s, e]) => ({
        start: s,
        end: e,
        quantity: quantities[rangeKey(s, e)] || stock,
      }));

    return maxBlockableForRange(start, end, {
      stock,
      reservations,
      ownerBlocks: otherBlocks,
      excludeRange: { start, end },
    }) > 0;
  };

  useEffect(() => {
    const payload = ownerBlockRanges
      .map(([start, end]) => {
        const max = getMaxBlockable(start, end);
        if (max <= 0) return null;
        const qty = quantities[rangeKey(start, end)] || stock;
        return [start, end, Math.min(qty, max)];
      })
      .filter(Boolean);
    onChange?.(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerBlockRanges, quantities]);

  const handleCalendarChange = (ranges) => {
    const last = ranges[ranges.length - 1];
    if (!last || last.length !== 2) return;

    const lastStart = new DateObject(last[0]).format();
    const lastEnd = new DateObject(last[1]).format();

    const formatted = formatCalendarRanges(ranges);
    const formattedOwnerRanges =
      stock === 1
        ? formatted.filter(([start, end]) => !isReservedRange([start, end]))
        : formatted;

    if (
      rangeIncludesLockedDay(lastStart, lastEnd) ||
      !canBlockRange(lastStart, lastEnd, formattedOwnerRanges)
    ) {
      return false;
    }

    setOwnerBlockRanges(formattedOwnerRanges);
    setQuantities((prev) => {
      const next = { ...prev };
      formattedOwnerRanges.forEach(([start, end]) => {
        const key = rangeKey(start, end);
        if (next[key] == null) {
          const otherBlocks = formattedOwnerRanges
            .filter(([s, e]) => s !== start || e !== end)
            .map(([s, e]) => ({
              start: s,
              end: e,
              quantity: next[rangeKey(s, e)] || stock,
            }));
          const max = maxBlockableForRange(start, end, {
            stock,
            reservations,
            ownerBlocks: otherBlocks,
            excludeRange: { start, end },
          });
          next[key] = Math.max(1, max);
        }
      });
      return next;
    });
  };

  const removeBlock = (start, end) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(t("confirmDeleteBlockedDates"))) return;
    setOwnerBlockRanges((prev) =>
      prev.filter(([rStart, rEnd]) => !(rStart === start && rEnd === end))
    );
    setQuantities((prev) => {
      const next = { ...prev };
      delete next[rangeKey(start, end)];
      return next;
    });
  };

  const updateQuantity = (start, end, value) => {
    const max = getMaxBlockable(start, end);
    const clamped =
      max > 0
        ? Math.max(1, Math.min(Number(value) || 1, max))
        : Math.max(1, Number(value) || 1);
    setQuantities((prev) => ({ ...prev, [rangeKey(start, end)]: clamped }));
  };

  const mapDay = ({ date }) => {
    const strDate = date.format();
    const { blocked, available } = getOccupancy(strDate);

    if (blocked > 0) {
      return { className: "in-service" };
    }

    if (available === 0) {
      return { disabled: true };
    }
  };

  const sortedReservations = useMemo(
    () => [...reservations].sort((a, b) => new Date(a.start) - new Date(b.start)),
    [reservations]
  );

  const sortedOwnerBlocks = useMemo(
    () =>
      ownerBlockRanges
        .slice()
        .sort((a, b) => new Date(a[0]) - new Date(b[0])),
    [ownerBlockRanges]
  );

  function OwnerBlocksPanel() {
    return (
      <VStack spacing={2} maxHeight="215px" overflowY="auto" align="stretch">
        {sortedOwnerBlocks.map(([start, end]) => {
          const max = getMaxBlockable(start, end);
          const qty = Math.min(
            quantities[rangeKey(start, end)] || max || stock,
            max > 0 ? max : quantities[rangeKey(start, end)] || stock
          );
          const isFullFleetBlock = qty >= stock;
          return (
            <HStack key={rangeKey(start, end)} justify="space-between">
              <VStack align="start" spacing={0}>
                <Button size="xs" variant="ghost">
                  {`${start} - ${end}`}
                </Button>
                {stock > 1 && (
                  <Text fontSize="xs" color="gray.600" pl={2}>
                    {isFullFleetBlock
                      ? t("blockingAllUnits", { stock })
                      : t("blockingPartialUnits", { qty, stock })}
                  </Text>
                )}
              </VStack>
              <HStack>
                {stock > 1 && (
                  <HStack>
                    <Text fontSize="xs" whiteSpace="nowrap">
                      {t("blockQuantityLabel")}
                    </Text>
                    <NumberInput
                      size="xs"
                      width="70px"
                      min={1}
                      max={max > 0 ? max : undefined}
                      value={qty}
                      onChange={(value) => updateQuantity(start, end, value)}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                      {`/ ${max > 0 ? max : 0}`}
                    </Text>
                  </HStack>
                )}
                <CloseButton size="sm" onClick={() => removeBlock(start, end)} />
              </HStack>
            </HStack>
          );
        })}
      </VStack>
    );
  }
  OwnerBlocksPanel.position = "right";

  return (
    <Box flexShrink={0}>
      <div style={{ margin: "10px 0" }}>
        <div className="un-availble">
          <div className="disabled-day-legend" />
          <p>{t("fullyBookedDisabledLegend")}</p>
        </div>
        <div className="un-availble">
          <div className="in-service-legend" />
          <p>{t("blockedDatesLegend")}</p>
        </div>
      </div>
      <Text fontSize="sm" mb={2}>
        {t("setUnavailableDatesForVehicle")}{" "}
        <Text as="span" fontWeight="bold" color="teal.600">
          (CET)
        </Text>
        :
      </Text>
      <Calendar
        className="teal vehicle-stock-calendar"
        multiple
        range
        currentDate={calendarActiveDate}
        onMonthChange={setCalendarActiveDate}
        minDate={new DateObject(todayDateInCET())}
        value={calendarValue}
        onChange={handleCalendarChange}
        mapDays={mapDay}
        plugins={[<OwnerBlocksPanel key="owner-blocks" />]}
      />

      {sortedReservations.length > 0 && (
        <Box mt={4}>
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            {t("reservedPeriodsLabel")}
          </Text>
          <VStack spacing={1} align="stretch" maxHeight="215px" overflowY="auto">
            {sortedReservations.map((res) => (
              <HStack
                key={rangeKey(res.start, res.end)}
                justify="space-between"
                fontSize="xs"
              >
                <Text>{`${res.start} - ${res.end}`}</Text>
                {stock > 1 ? (
                  <Text color="blue.600" fontWeight="semibold" whiteSpace="nowrap">
                    {t("reservedUnitsShort", {
                      booked: res.quantity,
                      stock,
                    })}
                  </Text>
                ) : null}
              </HStack>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

export default VehicleUnavailableDates;
